import { useState } from "react";
import * as LucideIcons from "lucide-react";
import { AlertTriangle, ArrowRight, Loader2, MessageCircle, MessageCircleOff, Package, Sparkles } from "lucide-react";
import { SEGMENTI, MERCATI, OPERAZIONI, COMPAT, SOTTOTIPI_BUSINESS, type WizardPayload } from "./WizardConfig";
import { BollettaUploadField } from "./BollettaUploadField";
import { DocumentoUploadField } from "./DocumentoUploadField";
import { validaEmail, validaTelefono } from "./recapiti";

type Props = {
  hasActiveSession: boolean;
  onResume: () => void;
  onSkip: () => void;
  onSubmit: (payload: WizardPayload, bolletta: File[], documento: File[]) => Promise<void>;
};

/** Risolve un nome icona PascalCase o kebab-case dal dizionario lucide-react. */
function Icon({ name, className }: { name: string; className?: string }) {
  // Normalizza kebab-case → PascalCase (es. "building-2" → "Building2")
  const pascal = name
    .split("-")
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join("");
  const C = ((LucideIcons as unknown) as Record<string, React.ComponentType<{ className?: string }>>)[pascal]
    ?? Package;
  return <C className={className} />;
}

export function ChatWizard({ hasActiveSession, onResume, onSkip, onSubmit }: Props) {
  const [segmento, setSegmento] = useState<string | null>(null);
  const [sottotipo, setSottotipo] = useState<string | null>(null);
  const [mercato, setMercato]   = useState<string | null>(null);
  const [operazione, setOp]     = useState<string | null>(null);
  const [recapito, setRecapito] = useState<"Cartacea" | "Email">("Email");
  // Recapiti del cliente, facoltativi. L'errore compare solo dopo che l'operatore
  // è uscito dalla casella (validazione al blur, non a ogni lettera).
  const [telefono, setTelefono] = useState("");
  const [email, setEmail] = useState("");
  const [toccati, setToccati] = useState<{ telefono: boolean; email: boolean }>({ telefono: false, email: false });
  const [bolletta, setBolletta] = useState<File[]>([]);
  const [documento, setDocumento] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const compat = segmento ? COMPAT[segmento] : null;
  // Documento identità richiesto per le operazioni che aprono pratica (alimenta subito
  // CF/intestatario da MRZ). Per "analisi" no: la chat lo chiede come fallback solo se
  // il cliente decide poi di procedere (documento_identita nella raccolta dati).
  const documentoRichiesto = operazione !== "analisi";
  const sottotipoRichiesto = segmento === "business";
  const telefonoEsito = validaTelefono(telefono);
  const emailEsito = validaEmail(email);
  const canSubmit = !!(segmento && mercato && operazione) && (!sottotipoRichiesto || !!sottotipo)
    && (!documentoRichiesto || documento.length > 0)
    && !telefonoEsito.errore && !emailEsito.errore
    && !submitting;

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(
        {
          segmento: segmento!, mercato: mercato!, operazione: operazione!,
          bolletta_uploaded: bolletta.length > 0, documento_uploaded: documento.length > 0, recapito,
          tipo_soggetto: sottotipoRichiesto ? sottotipo! : undefined,
          telefono: telefonoEsito.valore,
          email: emailEsito.valore,
        },
        bolletta,
        documento,
      );
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Errore avvio consulenza";
      setError(msg);
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-full bg-slate-50">
      {/* Topbar */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Sparkles className="w-4 h-4 text-[#4B9B2B]" />
          Nuova consulenza
        </div>
        {hasActiveSession && (
          <button
            onClick={onResume}
            className="text-sm text-slate-700 hover:text-[#4B9B2B] flex items-center gap-1.5 border border-slate-200 rounded-md px-3 py-1.5 bg-white"
          >
            <MessageCircle className="w-4 h-4" />
            Riprendi la consulenza in corso
          </button>
        )}
      </div>

      <div className="p-6 md:p-8 max-w-3xl mx-auto">
        <div className="flex items-center gap-2 mb-1">
          <Package className="w-5 h-5 text-slate-700" />
          <h1 className="text-2xl font-bold tracking-tight">Che consulenza apriamo?</h1>
        </div>
        <p className="text-slate-600 mb-6">
          Rispondi a 3 domande veloci. L&apos;assistente AI parte già con il contesto giusto. Puoi sempre saltare e parlare libero.
        </p>

        {/* Step 1: segmento */}
        <Section title="1 · Tipo cliente">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {SEGMENTI.map((s) => (
              <ChoiceCard
                key={s.id}
                c={s}
                selected={segmento === s.id}
                onClick={() => {
                  // Ri-cliccare la scheda già selezionata non deve azzerare il sottotipo
                  // scelto sotto: solo il cambio di segmento lo invalida.
                  if (s.id !== segmento) setSottotipo(null);
                  setSegmento(s.id);
                  const next = COMPAT[s.id];
                  if (mercato && !next.mercati.includes(mercato)) setMercato(null);
                  if (operazione && !next.operazioni.includes(operazione)) setOp(null);
                }}
              />
            ))}
          </div>

          {segmento === "business" && (
            <div className="mt-3">
              <p className="text-sm text-muted-foreground mb-2">Che tipo di azienda?</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {SOTTOTIPI_BUSINESS.map((s) => (
                  <ChoiceCard key={s.id} c={s} selected={sottotipo === s.id} onClick={() => setSottotipo(s.id)} />
                ))}
              </div>
            </div>
          )}
        </Section>

        {/* Step 2: mercato (filtered) */}
        <Section title="2 · Mercato" disabled={!segmento}>
          <div className="grid grid-cols-2 gap-3">
            {MERCATI.filter((m) => !compat || compat.mercati.includes(m.id)).map((m) => (
              <ChoiceCard key={m.id} c={m} selected={mercato === m.id} onClick={() => setMercato(m.id)} />
            ))}
          </div>
        </Section>

        {/* Step 3: operazione. I 4 tipi-pratica formano una griglia 2×2; "Analisi bolletta"
            (intent diverso: consulenza, non pratica) spana entrambe le colonne in fondo → pagina
            armonica anche con un numero dispari di voci. */}
        <Section title="3 · Tipo operazione" disabled={!mercato}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {OPERAZIONI.filter((o) => !compat || compat.operazioni.includes(o.id)).map((o) => (
              <ChoiceCard
                key={o.id}
                c={o}
                selected={operazione === o.id}
                onClick={() => setOp(o.id)}
                className={o.id === "analisi" ? "sm:col-span-2" : undefined}
              />
            ))}
          </div>
        </Section>

        {/* Step 4: recapito bolletta */}
        <Section title="4 · Recapito bolletta" disabled={!operazione}>
          <div className="grid grid-cols-2 gap-3">
            {(["Email", "Cartacea"] as const).map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => setRecapito(opt)}
                className={`text-left border-2 rounded-lg p-4 flex items-center gap-3 bg-white transition-colors ${
                  recapito === opt
                    ? "border-[#4B9B2B] bg-[#4B9B2B]/5"
                    : "border-slate-200 hover:border-[#4B9B2B]/60"
                }`}
              >
                <span className={`flex-none w-10 h-10 rounded-lg grid place-items-center ${
                  opt === "Cartacea" ? "bg-slate-100 text-slate-700" : "bg-blue-100 text-blue-700"
                }`}>
                  <Icon name={opt === "Cartacea" ? "Mail" : "AtSign"} className="w-5 h-5" />
                </span>
                <span>
                  <span className="block font-semibold">{opt}</span>
                  <span className="block text-xs text-muted-foreground">
                    {opt === "Cartacea" ? "Bolletta cartacea per posta" : "Bolletta inviata via email"}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </Section>

        {/* Step 5: recapiti del cliente, facoltativi. Dati scritti a mano prima dei file
            da allegare; l'email sta un rigo sotto la scelta «bolletta via email». Vuoti →
            la chat li chiede come sempre; compilati → la chat li trova già e non li chiede. */}
        <Section title="5 · Recapiti del cliente · facoltativi" disabled={!operazione}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <CampoRecapito
              id="wizard-telefono"
              label="Telefono"
              type="tel"
              placeholder="333 1234567"
              aiuto="Cellulare del cliente, con o senza +39"
              value={telefono}
              onChange={setTelefono}
              onBlur={() => setToccati((t) => ({ ...t, telefono: true }))}
              errore={toccati.telefono ? telefonoEsito.errore : undefined}
              disabled={submitting}
            />
            <CampoRecapito
              id="wizard-email"
              label="Email"
              type="email"
              placeholder="nome@dominio.it"
              aiuto="Dove il cliente riceve le comunicazioni"
              value={email}
              onChange={setEmail}
              onBlur={() => setToccati((t) => ({ ...t, email: true }))}
              errore={toccati.email ? emailEsito.errore : undefined}
              disabled={submitting}
            />
          </div>
        </Section>

        {/* Step 6 (ex-5): bolletta opzionale */}
        <Section title="Bolletta del cliente · opzionale, velocizza analisi" disabled={!operazione}>
          <BollettaUploadField
            files={bolletta}
            onChange={setBolletta}
            disabled={submitting}
          />
        </Section>

        {/* Step 7 (ex-6): documento identità — obbligatorio per le pratiche, opzionale per analisi */}
        <Section
          title={documentoRichiesto
            ? "Documento d'identità del cliente · obbligatorio"
            : "Documento d'identità del cliente · opzionale per l'analisi"}
          disabled={!operazione}
        >
          <DocumentoUploadField
            files={documento}
            onChange={setDocumento}
            disabled={submitting}
          />
        </Section>

        {error && (
          <div
            role="alert"
            className="mb-3 flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800"
          >
            <AlertTriangle className="w-4 h-4 flex-none mt-0.5" aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        {documentoRichiesto && documento.length === 0 && (
          <p className="text-xs text-amber-600 mb-3">Carica il documento d&apos;identità per procedere.</p>
        )}

        <div className="flex items-center justify-between border-t border-slate-200 pt-5 mt-4">
          <button
            onClick={onSkip}
            disabled={submitting}
            className="text-sm text-slate-600 hover:text-[#4B9B2B] flex items-center gap-1.5 disabled:opacity-50"
          >
            <MessageCircleOff className="w-4 h-4" />
            Salta le domande
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            aria-busy={submitting}
            className="bg-[#4B9B2B] hover:bg-[#3a7a21] disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold rounded-md px-6 py-2.5 flex items-center gap-2 transition-colors"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Avvio in corso…
              </>
            ) : (
              <>
                Avvia consulenza
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({
  title,
  disabled,
  children,
}: {
  title: string;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={`mb-6 ${disabled ? "opacity-40 pointer-events-none" : ""}`}>
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{title}</div>
      {children}
    </div>
  );
}

/** Casella di testo con etichetta visibile, riga d'aiuto ed errore sotto il campo.
 *  autocomplete spento: sono i recapiti del CLIENTE, il browser proporrebbe quelli
 *  dell'operatore. type tel/email apre la tastiera giusta sul telefono. */
function CampoRecapito({
  id,
  label,
  type,
  placeholder,
  aiuto,
  value,
  onChange,
  onBlur,
  errore,
  disabled,
}: {
  id: string;
  label: string;
  type: "tel" | "email";
  placeholder: string;
  aiuto: string;
  value: string;
  onChange: (v: string) => void;
  onBlur: () => void;
  errore?: string;
  disabled?: boolean;
}) {
  const descrizioneId = `${id}-${errore ? "errore" : "aiuto"}`;
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-slate-700 mb-1">
        {label}
      </label>
      <input
        id={id}
        type={type}
        inputMode={type}
        autoComplete="off"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        disabled={disabled}
        aria-invalid={!!errore}
        aria-describedby={descrizioneId}
        className={`w-full text-base border-2 rounded-lg px-4 py-2.5 bg-white transition-colors focus:outline-none ${
          errore
            ? "border-red-300 focus:border-red-500"
            : "border-slate-200 hover:border-[#4B9B2B]/60 focus:border-[#4B9B2B]"
        } disabled:opacity-50`}
      />
      {errore ? (
        <p id={descrizioneId} role="alert" className="text-xs text-red-700 mt-1 flex items-center gap-1">
          <AlertTriangle className="w-3.5 h-3.5 flex-none" aria-hidden="true" />
          {errore}
        </p>
      ) : (
        <p id={descrizioneId} className="text-xs text-slate-500 mt-1">{aiuto}</p>
      )}
    </div>
  );
}

function ChoiceCard({
  c,
  selected,
  onClick,
  className,
}: {
  c: { label: string; description?: string; icon: string; iconBg: string; iconFg: string };
  selected: boolean;
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`text-left border-2 rounded-lg p-4 flex items-center gap-3 bg-white transition-colors ${
        selected
          ? "border-[#4B9B2B] bg-[#4B9B2B]/5"
          : "border-slate-200 hover:border-[#4B9B2B]/60"
      } ${className ?? ""}`}
    >
      <span className={`flex-none w-10 h-10 rounded-lg grid place-items-center ${c.iconBg} ${c.iconFg}`}>
        <Icon name={c.icon} className="w-5 h-5" />
      </span>
      <span>
        <span className="block font-semibold">{c.label}</span>
        {c.description && <span className="block text-xs text-slate-500">{c.description}</span>}
      </span>
    </button>
  );
}
